import { Link, useNavigate } from "react-router-dom";
import { Play, Volume2, Timer, Rocket, Ruler, Heart, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { GlassCard } from "./ui";
import { StockBadge, formatGBP } from "./StockBadge";
import { Button } from "../ui/button";
import { useShop } from "../../context/ShopContext";
import { bestImg, onImgError } from "../../lib/img";

export default function ProductCard({ product, index = 0 }) {
  const navigate = useNavigate();
  const { addToBasket, isFav, toggleFav } = useShop();
  const img = (product.images || []).find((m) => m.is_primary) || (product.images || [])[0];
  const specs = product.specs || {};
  const hasVideo = !!(product.youtube_url || product.vimeo_url);
  const reservable = product.stock_status === "in_stock";
  const fav = isFav(product.product_id);
  const promo = product.promotion;
  const pp = promo?.pricing;
  const showPromoPrice = pp?.per_unit && pp.saving > 0;
  const saving = product.compare_at_price && product.compare_at_price > product.retail_price
    ? product.compare_at_price - product.retail_price : 0;

  const onFav = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const res = await toggleFav(product);
    if (res?.needLogin) { toast.message("Sign in to save favourites"); navigate(`/login?next=/product/${product.slug}`); }
  };

  return (
    <GlassCard className="p-0 overflow-hidden flex flex-col group" data-testid={`product-card-${product.slug}`}>
      <Link to={`/product/${product.slug}`} className="relative block aspect-[4/3] overflow-hidden bg-black/40">
        {img ? (
          <img src={bestImg(img)} onError={onImgError(img)} alt={img.alt || product.name} className="h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-105" loading="lazy" />
        ) : <div className="h-full w-full grid place-items-center text-white/20"><Rocket className="h-10 w-10" /></div>}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
          {promo && <span data-testid={`promo-ribbon-${product.slug}`} className="text-[10px] font-bold bg-gold text-ink px-2 py-0.5 rounded-full shadow-lg">{promo.ribbon}</span>}
          <StockBadge status={product.stock_status} />
          {product.is_featured && <span className="text-[10px] font-bold bg-white/90 text-ink px-2 py-0.5 rounded-full">FEATURED</span>}
          {product.is_best_seller && <span className="text-[10px] font-bold bg-fireorange text-ink px-2 py-0.5 rounded-full">BEST SELLER</span>}
          {product.is_new && <span className="text-[10px] font-bold bg-white/90 text-ink px-2 py-0.5 rounded-full">NEW</span>}
          {!promo && saving > 0 && <span className="text-[10px] font-bold bg-firered text-white px-2 py-0.5 rounded-full" data-testid={`save-badge-${product.slug}`}>SAVE {formatGBP(saving)}</span>}
        </div>
        <button onClick={onFav} data-testid={`fav-btn-${product.slug}`} aria-label="Toggle favourite"
          className={`absolute top-2.5 right-2.5 grid place-items-center h-8 w-8 rounded-full backdrop-blur transition-colors ${fav ? "bg-firered/90 text-white" : "bg-black/50 text-white/80 hover:text-white"}`}>
          <Heart className="h-4 w-4" fill={fav ? "currentColor" : "none"} />
        </button>
        {hasVideo && <div className="absolute bottom-2.5 right-2.5 grid place-items-center h-8 w-8 rounded-full bg-black/60 text-white"><Play className="h-4 w-4" /></div>}
      </Link>

      <div className="p-4 flex flex-col flex-1">
        {product.brand && <p className="text-[11px] tracking-wide uppercase text-white/40">{product.brand}</p>}
        <Link to={`/product/${product.slug}`}><h3 className="font-playfair text-base text-white leading-tight line-clamp-2 hover:text-gold transition-colors">{product.name}</h3></Link>
        {product.short_description && <p className="mt-1 text-xs text-white/50 line-clamp-2" data-testid={`card-desc-${product.slug}`}>{product.short_description}</p>}

        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/50">
          {specs.shots != null && <span className="flex items-center gap-1"><Rocket className="h-3 w-3" /> {specs.shots} shot{specs.shots !== 1 ? "s" : ""}</span>}
          {specs.duration_seconds != null && <span className="flex items-center gap-1"><Timer className="h-3 w-3" /> {specs.duration_seconds}s</span>}
          {specs.noise_level && <span className="flex items-center gap-1 capitalize"><Volume2 className="h-3 w-3" /> {specs.noise_level}</span>}
          {specs.safety_distance_m != null && <span className="flex items-center gap-1"><Ruler className="h-3 w-3" /> {specs.safety_distance_m}m</span>}
        </div>

        <div className="mt-auto pt-4 flex items-center justify-between gap-2">
          <div>
            {showPromoPrice ? (
              <>
                <span className="block text-sm font-semibold text-white/50 line-through mr-1.5" data-testid={`card-was-${product.slug}`}>{formatGBP(pp.original_price)}</span>
                <span className="font-playfair text-lg text-gold" data-testid={`card-price-${product.slug}`}>{formatGBP(pp.current_price)}</span>
              </>
            ) : (
              <>
                {saving > 0 && <span className="block text-sm font-semibold text-firered line-through decoration-firered mr-1.5" data-testid={`card-rrp-${product.slug}`}>RRP {formatGBP(product.compare_at_price)}</span>}
                <span className="font-playfair text-lg text-gold" data-testid={`card-price-${product.slug}`}>{formatGBP(product.retail_price)}</span>
              </>
            )}
          </div>
          <Button asChild size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10">
            <Link to={`/product/${product.slug}`} data-testid={`view-product-${product.slug}`}>View</Link>
          </Button>
        </div>
        <Button disabled={!reservable} onClick={() => addToBasket(product)} data-testid={`add-basket-${product.slug}`} size="sm"
          className={`mt-2 w-full font-semibold gap-2 ${reservable ? "cf-gold-btn" : "opacity-60 cursor-not-allowed cf-gold-btn"}`}>
          <ShoppingBag className="h-4 w-4" /> {reservable ? "Add to Basket" : "Not available"}
        </Button>
      </div>
    </GlassCard>
  );
}
